import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import ProtectedEmail from "@/components/ProtectedEmail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgets = [
  "500 € – 1.500 €",
  "1.500 € – 3.000 €",
  "ab 3.000 €",
  "individuelle Anfrage",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", budget: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Bitte Namen angeben.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Gültige E-Mail erforderlich.";
    if (form.message.trim().length < 10) er.message = "Bitte etwas mehr Kontext (min. 10 Zeichen).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        budget: form.budget || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Anfrage erhalten — wir melden uns innerhalb von 24 Stunden.");
      setForm({ name: "", email: "", budget: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Etwas ist schiefgelaufen. Bitte später erneut versuchen.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 border-t border-white/[0.05] overflow-hidden"
    >
      <div className="absolute left-1/2 -translate-x-1/2 -top-20 w-[700px] h-[400px] glow-blue opacity-50 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
            ↳ Kontakt
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-3 font-display text-3xl sm:text-5xl text-white tracking-tight leading-[1.05]"
          >
            Lassen Sie uns etwas <span className="text-gradient-blue">Starkes</span> aufbauen.
          </motion.h2>
          <p className="mt-5 text-zinc-400 text-base">
            Unverbindliche Anfrage · ehrliche Einschätzung · schnelle Rückmeldung.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <ProtectedEmail variant="card" testId="contact-email-card" />

            <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Erstgespräch</div>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                30 Minuten, kostenlos, unverbindlich. Wir hören zu, geben ehrliche
                Einschätzung und sagen auch ehrlich, wenn wir nicht der richtige
                Partner sind.
              </p>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="contact-form"
            className="lg:col-span-7 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-7 backdrop-blur-sm"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                id="name"
                label="Name"
                value={form.name}
                onChange={update("name")}
                error={errors.name}
                testId="contact-input-name"
                placeholder="Ihr Name"
              />
              <Field
                id="email"
                label="E-Mail"
                type="email"
                value={form.email}
                onChange={update("email")}
                error={errors.email}
                testId="contact-input-email"
                placeholder="ihre@firma.de"
              />
            </div>

            <div className="mt-4">
              <Label htmlFor="budget">Projektbudget (optional)</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {budgets.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`contact-budget-${b}`}
                    onClick={() => setForm((f) => ({ ...f, budget: f.budget === b ? "" : b }))}
                    className={`text-[12px] px-3 py-1.5 rounded-full border transition-all duration-300 ${
                      form.budget === b
                        ? "bg-white text-black border-white"
                        : "border-white/10 text-zinc-300 hover:border-white/30 bg-white/[0.02]"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="message">Nachricht</Label>
              <textarea
                id="message"
                rows={5}
                value={form.message}
                onChange={update("message")}
                data-testid="contact-input-message"
                placeholder="Erzählen Sie uns kurz von Ihrem Vorhaben…"
                className={`mt-2 w-full bg-[#0a0a0b] border rounded-xl px-4 py-3 text-white placeholder-zinc-600 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all resize-none ${
                  errors.message ? "border-red-500/50" : "border-white/[0.08]"
                }`}
              />
              {errors.message && (
                <p className="mt-1.5 text-xs text-red-400">{errors.message}</p>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between gap-4">
              <p className="text-[11px] text-zinc-500 leading-relaxed max-w-xs">
                Mit dem Senden erklären Sie sich mit unserer{" "}
                <a href="/datenschutz" className="underline underline-offset-2 hover:text-zinc-300">
                  Datenschutzerklärung
                </a>{" "}
                einverstanden.
              </p>
              <button
                type="submit"
                disabled={loading || sent}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-medium text-sm hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Senden…
                  </>
                ) : sent ? (
                  <>Anfrage gesendet ✓</>
                ) : (
                  <>
                    Anfrage senden
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Label({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold"
    >
      {children}
    </label>
  );
}

function Field({ id, label, value, onChange, error, type = "text", placeholder, testId }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className={`mt-2 w-full bg-[#0a0a0b] border rounded-xl px-4 py-3 text-white placeholder-zinc-600 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all ${
          error ? "border-red-500/50" : "border-white/[0.08]"
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}
